import { ArrowRight, ShieldOff, ShoppingCart } from "lucide-react";
import { Link } from "react-router";

import { KpiCard } from "@/components/kpi-card";
import { StatusPill } from "@/components/status-pill";
import { Card, CardContent } from "@/components/ui/card";
import type {
  AdminAccountSummary,
  AdminMcpAnalytics,
  McpAccountUsage,
  McpDeniedUsage,
  McpToolUsage,
} from "@/lib/api";
import { cn } from "@/lib/utils";

import { McpAuditExplorer } from "./mcp-audit-explorer";
import { McpUsageChart } from "./mcp-usage-chart";

interface Props {
  analytics: AdminMcpAnalytics;
  accounts: AdminAccountSummary[];
  tools: string[];
  days: number;
}

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

// Lifecycle order for the order funnel: orders awaiting sign-off surface first,
// then the submitted headline, then every other status in arrival order.
const FUNNEL_STAGE_RANK: Record<string, number> = {
  PENDING_APPROVAL: 0,
  SUBMITTED: 1,
};

// Tint the two attention states with palette shades already used for
// pending/blocked signals elsewhere in this dashboard; all others stay neutral.
const FUNNEL_STAGE_TONE: Record<string, string> = {
  PENDING_APPROVAL: "text-jj-orange-05",
  REJECTED: "text-jj-maroon-05",
};

/** Compact relative time, e.g. "3h ago" / "2d ago". Falls back to a date. */
function relativeTime(iso: string | null | undefined): string {
  if (!iso) {
    return "never";
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return "—";
  }
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) {
    return "just now";
  }
  const mins = Math.round(diffSec / 60);
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hours = Math.round(mins / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const dayCount = Math.round(hours / 24);
  if (dayCount < 30) {
    return `${dayCount}d ago`;
  }
  return new Date(iso).toLocaleDateString();
}

export function McpDashboard({ analytics, accounts, tools, days }: Props) {
  const {
    summary,
    timeseries,
    tools: toolUsage,
    accounts: accountUsage,
    denied,
    order_funnel,
  } = analytics;

  const funnel = [...order_funnel].sort(
    (a, b) => (FUNNEL_STAGE_RANK[a.header_status] ?? 2) - (FUNNEL_STAGE_RANK[b.header_status] ?? 2),
  );

  return (
    <div className="space-y-8">
      <section>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <KpiCard label="Total calls" value={summary.total_calls} caption={`Last ${days} days`} />
          <KpiCard
            label="Denied"
            value={summary.denied_calls}
            caption="Blocked by permissions"
            valueClassName={summary.denied_calls > 0 ? "text-jj-maroon-05" : undefined}
          />
          <KpiCard label="Errors" value={summary.error_calls} caption="Failed calls" />
          <KpiCard
            label="Active users"
            value={summary.active_users}
            caption={`${summary.active_accounts} accounts`}
          />
          <KpiCard
            label="Avg latency"
            value={
              summary.avg_latency_ms == null ? "—" : `${Math.round(summary.avg_latency_ms)} ms`
            }
            caption="Across all calls"
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
          Usage over time
        </h3>
        <Card>
          <CardContent className="py-6">
            <McpUsageChart data={timeseries} />
            <div className="text-muted mt-3 flex items-center justify-between text-xs">
              <span className="font-mono text-[10px] tabular-nums">{timeseries[0]?.day}</span>
              <span className="flex items-center gap-3">
                <span className="text-muted inline-flex items-center gap-1.5 text-[11px]">
                  <span className="bg-jj-green-03 h-2.5 w-2.5 rounded-sm" /> allowed
                </span>
                <span className="text-muted inline-flex items-center gap-1.5 text-[11px]">
                  <span className="bg-jj-maroon-05 h-2.5 w-2.5 rounded-sm" /> denied
                </span>
                <span className="text-muted inline-flex items-center gap-1.5 text-[11px]">
                  <span className="bg-warm-gray-05 h-2.5 w-2.5 rounded-sm" /> errors
                </span>
              </span>
              <span className="font-mono text-[10px] tabular-nums">
                {timeseries[timeseries.length - 1]?.day}
              </span>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
            By tool
          </h3>
          <Card>
            <CardContent className="space-y-3 py-4">
              {toolUsage.length === 0 ? (
                <p className="text-muted text-sm">No tool calls in this window.</p>
              ) : (
                toolUsage.map((t: McpToolUsage) => (
                  <div
                    key={t.tool_name}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="text-foreground truncate font-mono text-xs">
                        {t.tool_name}
                      </span>
                      <span className="text-muted text-xs">
                        {t.avg_latency_ms == null ? "—" : `${Math.round(t.avg_latency_ms)} ms avg`}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <span className="text-foreground text-sm font-medium">{t.total}</span>
                      <StatusPill tone="green">ok {t.ok}</StatusPill>
                      {t.denied > 0 && <StatusPill tone="maroon">denied {t.denied}</StatusPill>}
                      {t.errors > 0 && <StatusPill tone="orange">err {t.errors}</StatusPill>}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-3">
          <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
            Top customers
          </h3>
          <Card>
            <CardContent className="space-y-3 py-4">
              {accountUsage.length === 0 ? (
                <p className="text-muted text-sm">No customer activity in this window.</p>
              ) : (
                accountUsage.map((a: McpAccountUsage) => (
                  <div
                    key={a.account_id}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <div className="flex min-w-0 flex-col">
                      <Link
                        to={`/admin/accounts/${a.account_id}`}
                        className="text-foreground hover:text-accent truncate font-medium underline-offset-2 hover:underline"
                      >
                        {a.account_name}
                      </Link>
                      <span className="text-muted text-xs">{relativeTime(a.last_call_at)}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <span className="text-foreground text-sm font-medium">{a.total}</span>
                      {a.denied > 0 && <StatusPill tone="maroon">denied {a.denied}</StatusPill>}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
          Orders placed via MCP
        </h3>
        <Card>
          <CardContent className="py-4">
            {funnel.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-1.5 py-8 text-center">
                <ShoppingCart className="text-muted h-6 w-6" />
                <p className="text-foreground text-sm font-medium">No orders yet</p>
                <p className="text-muted max-w-xs text-xs">
                  Orders customers place through the agent will show up here by status.
                </p>
              </div>
            ) : (
              <div className="divide-border divide-y">
                {funnel.map((stage) => (
                  <div
                    key={stage.header_status}
                    className="flex items-center justify-between gap-3 py-2.5 text-sm first:pt-0 last:pb-0"
                  >
                    <span
                      className={cn(
                        "font-medium",
                        FUNNEL_STAGE_TONE[stage.header_status] ?? "text-foreground",
                      )}
                    >
                      {stage.header_status}
                    </span>
                    <div className="flex items-center gap-4">
                      <span className="text-muted">
                        {stage.count} order{stage.count === 1 ? "" : "s"}
                      </span>
                      <span className="text-foreground font-medium tabular-nums">
                        {stage.total_value_usd == null ? "—" : usd.format(stage.total_value_usd)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {denied.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
            Denied calls
          </h3>
          <Card>
            <CardContent className="space-y-2 py-4">
              {denied.map((d: McpDeniedUsage) => (
                <div
                  key={`${d.account_id}:${d.tool_name}`}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <ShieldOff className="text-jj-maroon-05 h-4 w-4 shrink-0" />
                    <span className="text-foreground truncate">
                      {d.account_name}
                      <span className="text-muted"> · </span>
                      <span className="font-mono text-xs">{d.tool_name}</span>
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <StatusPill tone="maroon">denied {d.denied}</StatusPill>
                    <Link
                      to={`/admin/accounts/${d.account_id}`}
                      className="text-accent inline-flex items-center gap-0.5 text-xs underline-offset-2 hover:underline"
                    >
                      tune grants
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      <section className="space-y-3">
        <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
          Audit feed
        </h3>
        <McpAuditExplorer accounts={accounts} tools={tools} />
      </section>
    </div>
  );
}
